<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :minimized-width="minimizedWidth"
    :minimized="isMinimized"
    :animated="true"
  >
    <div class="sidebar-menu-header" :class="{ 'sidebar-menu-header--minimized': isMinimized }">
      <span v-if="!isMinimized" class="sidebar-menu-header__title">Main Menu</span>
      <button
        type="button"
        class="sidebar-menu-header__toggle"
        :aria-label="isMinimized ? 'Expand menu' : 'Collapse menu'"
        @click="toggleMinimized"
      >
        <VaIconMenuCollapsed
          class="sidebar-menu-header__icon"
          :class="{ 'x-flip': !isMinimized }"
          color="rgba(231, 238, 252, 0.9)"
        />
      </button>
    </div>

    <template v-if="isMinimized">
      <div class="collapsed-sidebar">
        <template v-for="(item, index) in clientRoutes" :key="index">
          <template v-if="item.children?.length">
            <VaSidebarItem
              v-for="child in item.children"
              :key="child.name"
              :to="{ name: child.name }"
              :active="isActive(child)"
              role="button"
              hover-opacity="0.1"
              class="collapsed-item"
              :title="child.displayName"
            >
              <VaSidebarItemContent class="py-3 px-2 flex justify-center">
                <VaIcon
                  v-if="child.meta?.icon"
                  :name="child.meta.icon"
                  size="24px"
                  class="sidebar-nav-icon"
                />
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>
          <VaSidebarItem
            v-else
            :to="{ name: item.name }"
            :active="isActive(item)"
            role="button"
            hover-opacity="0.1"
            class="collapsed-item"
            :title="item.displayName"
          >
            <VaSidebarItemContent class="py-3 px-2 flex justify-center">
              <VaIcon v-if="item.meta?.icon" :name="item.meta.icon" size="24px" class="sidebar-nav-icon" />
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
      </div>
    </template>

    <template v-else>
      <div class="client-sidebar-list">
        <template v-for="(item, index) in clientRoutes" :key="index">
          <div v-if="item.children?.length" class="nav-group">
            <button
              type="button"
              class="nav-group__toggle"
              :class="{ 'nav-group__toggle--open': isGroupOpen(item) }"
              @click="toggleGroup(item.name)"
            >
              <VaIcon v-if="item.meta?.icon" :name="item.meta.icon" size="20px" class="sidebar-nav-icon" />
              <span class="nav-group__title">{{ item.displayName }}</span>
              <VaIcon
                name="expand_more"
                size="18px"
                class="nav-group__chevron"
                :class="{ 'nav-group__chevron--open': isGroupOpen(item) }"
              />
            </button>
            <div v-show="isGroupOpen(item)" class="nav-group__children">
              <VaSidebarItem
                v-for="child in item.children"
                :key="child.name"
                :to="{ name: child.name }"
                :active="isActive(child)"
                role="button"
                hover-opacity="0.1"
                class="nav-group__child"
              >
                <VaSidebarItemContent class="py-2.5 pr-2 pl-4">
                  <VaIcon
                    v-if="child.meta?.icon"
                    :name="child.meta.icon"
                    size="18px"
                    class="sidebar-nav-icon"
                  />
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ child.displayName }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </div>

          <VaSidebarItem
            v-else
            :to="{ name: item.name }"
            :active="isActive(item)"
            role="button"
            hover-opacity="0.1"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon v-if="item.meta?.icon" :name="item.meta.icon" size="20px" class="sidebar-nav-icon" />
              <VaSidebarItemTitle class="leading-5 font-semibold">
                {{ item.displayName }}
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
      </div>
    </template>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ClientPortalNavigationRoutes, { type INavigationRoute } from './ClientPortalNavigationRoutes'
import VaIconMenuCollapsed from '@/components/icons/VaIconMenuCollapsed.vue'
import { listClientVehicles } from '@/services/clientPortalApi'

const CHARGING_DASHBOARD_ROUTE = 'contractor-charging-dashboard'
const VEHICLE_ACTIVITY_ROUTE = 'contractor-vehicle-activity'
const FLEET_DASHBOARD_ROUTES = new Set([CHARGING_DASHBOARD_ROUTE, VEHICLE_ACTIVITY_ROUTE])

export default defineComponent({
  name: 'ClientPortalSidebar',
  components: { VaIconMenuCollapsed },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible', 'update:minimized'],

  setup(props, { emit }) {
    const route = useRoute()
    const openGroups = ref<Record<string, boolean>>({})
    const showChargingDashboard = ref(true)

    onMounted(async () => {
      try {
        const vehicles = await listClientVehicles()
        showChargingDashboard.value = vehicles.length > 0
      } catch {
        showChargingDashboard.value = true
      }
    })

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActive = (r: INavigationRoute) => route.name === r.name

    const hasActiveChild = (item: INavigationRoute) =>
      Boolean(item.children?.some((child) => route.name === child.name))

    const isGroupOpen = (item: INavigationRoute) => {
      const explicit = openGroups.value[item.name]
      if (typeof explicit === 'boolean') return explicit
      return hasActiveChild(item)
    }

    function toggleGroup(name: string) {
      const item = ClientPortalNavigationRoutes.routes.find((r) => r.name === name)
      if (!item) return
      openGroups.value = {
        ...openGroups.value,
        [name]: !isGroupOpen(item),
      }
    }

    watch(
      () => route.name,
      () => {
        for (const item of ClientPortalNavigationRoutes.routes) {
          if (item.children?.some((child) => child.name === route.name)) {
            openGroups.value = { ...openGroups.value, [item.name]: true }
          }
        }
      },
      { immediate: true },
    )

    const isMinimized = computed(() => props.minimized)

    function toggleMinimized() {
      emit('update:minimized', !props.minimized)
    }

    const minimizedWidth = computed(() => '64px')
    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      if (isMinimized.value) return minimizedWidth.value
      return '280px'
    })

    const clientRoutes = computed(() =>
      ClientPortalNavigationRoutes.routes
        .map((item) => {
          if (!item.children?.length) return item
          if (showChargingDashboard.value) return item

          const children = item.children.filter((child) => !FLEET_DASHBOARD_ROUTES.has(child.name))
          if (!children.length) return null
          if (children.length === item.children.length) return item
          return { ...item, children }
        })
        .filter((item): item is INavigationRoute => item != null),
    )

    return {
      writableVisible,
      sidebarWidth,
      minimizedWidth,
      isMinimized,
      clientRoutes,
      isActive,
      isGroupOpen,
      toggleGroup,
      toggleMinimized,
    }
  },
})
</script>

<style scoped>
.va-sidebar-item-title {
  text-transform: none;
}

.va-sidebar {
  /* Match screenshot: dark/navy gradient sidebar */
  background: radial-gradient(1200px 900px at 0% 0%, #1b2a55 0%, #0b1220 55%, #070b14 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.client-sidebar-list :deep(.va-sidebar-item) {
  border-radius: 10px;
  margin: 0.15rem 0.5rem;
}

.client-sidebar-list :deep(.va-sidebar-item--active) {
  background: rgba(52, 114, 240, 0.22);
  border: 1px solid rgba(52, 114, 240, 0.35);
}

.client-sidebar-list :deep(.va-sidebar-item-title) {
  color: rgba(231, 238, 252, 0.82);
  font-weight: 700;
}

.client-sidebar-list :deep(.va-sidebar-item--active .va-sidebar-item-title) {
  color: #ffffff;
}

.client-sidebar-list :deep(.sidebar-nav-icon) {
  color: rgba(231, 238, 252, 0.72);
}

.client-sidebar-list :deep(.va-sidebar-item--active .sidebar-nav-icon) {
  color: #6ea8ff;
}

.nav-group {
  margin: 0.15rem 0.5rem;
}

.nav-group__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 1rem;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: rgba(231, 238, 252, 0.82);
  cursor: pointer;
  text-align: left;
}

.nav-group__toggle:hover,
.nav-group__toggle--open {
  background: rgba(255, 255, 255, 0.06);
}

.nav-group__title {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
}

.nav-group__chevron {
  color: rgba(231, 238, 252, 0.55);
  transition: transform 0.15s ease;
}

.nav-group__chevron--open {
  transform: rotate(180deg);
}

.nav-group__children {
  display: flex;
  flex-direction: column;
  padding: 0.1rem 0 0.25rem 0.65rem;
}

.nav-group__child {
  margin: 0.1rem 0 !important;
}

.collapsed-sidebar :deep(.va-sidebar-item) {
  border-radius: 12px;
  margin: 0.25rem 0.5rem;
}

.collapsed-sidebar :deep(.va-sidebar-item--active) {
  background: rgba(52, 114, 240, 0.22);
  border: 1px solid rgba(52, 114, 240, 0.35);
}

.collapsed-sidebar :deep(.sidebar-nav-icon) {
  color: rgba(231, 238, 252, 0.72);
}

.collapsed-sidebar :deep(.va-sidebar-item--active .sidebar-nav-icon) {
  color: #6ea8ff;
}

.client-sidebar-list {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}

.collapsed-sidebar {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
}

.collapsed-item {
  margin: 0.25rem 0;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.collapsed-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.collapsed-item .va-sidebar-item-content {
  padding: 0.75rem 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.sidebar-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1rem 0.65rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.sidebar-menu-header--minimized {
  justify-content: center;
  padding-inline: 0.5rem;
}

.sidebar-menu-header__title {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(231, 238, 252, 0.72);
}

.sidebar-menu-header__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.sidebar-menu-header__toggle:hover {
  background: rgba(255, 255, 255, 0.12);
}

.sidebar-menu-header__icon {
  width: 20px;
  height: 20px;
}

.x-flip {
  transform: scaleX(-1);
}
</style>
